/* eslint-disable no-restricted-imports */
import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import { type VariantProps, createTV, defaultConfig } from 'tailwind-variants';

/* eslint-enable no-restricted-imports */

type MergeConfig = typeof defaultConfig.twMergeConfig;

/**
 * Shared `tailwind-merge` config for `cn` and `tv`.
 *
 * Disables `font-size` conflict detection (both
 * `conflictingClassGroupModifiers` and `conflictingClassGroups` for
 * `font-size` are emptied) so responsive variants like
 * `text-sm md:text-base` can coexist on the same element instead of one
 * being deduplicated away.
 */
const twMergeConfig: MergeConfig = {
  override: {
    conflictingClassGroupModifiers: { 'font-size': [] },
    conflictingClassGroups: { 'font-size': [] },
  },
  extend: {
    classGroups: {},
  },
};
const twMerge = extendTailwindMerge(twMergeConfig);

// Mutate `defaultConfig.twMergeConfig` at module load so every
// `tailwind-variants` instance (including ones created via `createTV`
// elsewhere in the app) picks up the same merge config automatically.
// Importing this module anywhere is sufficient to install it.
defaultConfig.twMergeConfig = twMergeConfig;

/**
 * A `tailwind-variants` factory bound to the shared {@link twMergeConfig}.
 */
export const tv = createTV({
  twMergeConfig,
});

/**
 * Conditionally join class names and resolve Tailwind conflicts using the
 * shared {@link twMergeConfig} (combines `clsx` with `tailwind-merge`).
 */
export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

export { type ClassValue, type VariantProps };
